import { Check } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import CTA from "@/components/CTA";
import { SERVICES, SECTORS, PROCESS } from "@/data/content";

function ServiceBlock({ service, flip }) {
  return (
    <Reveal>
      <div
        data-testid={`service-detail-${service.id}`}
        className={`grid lg:grid-cols-2 gap-10 lg:gap-16 py-16 sm:py-20 border-t border-zinc-800/60 items-start`}
      >
        <div className={flip ? "lg:order-2" : ""}>
          <span className="font-display text-6xl sm:text-7xl font-extrabold text-zinc-800">{service.num}</span>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-zinc-100 leading-tight">
            {service.title}
          </h2>
          <p className="mt-5 text-base text-zinc-300 font-light leading-relaxed">{service.short}</p>
          <p className="mt-4 text-sm text-zinc-500 font-light leading-relaxed">{service.text}</p>
        </div>
        <div className={`border border-zinc-800/80 bg-void-2/30 p-8 sm:p-10 ${flip ? "lg:order-1" : ""}`}>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mb-6">Key deliverables</p>
          <ul className="space-y-4">
            {service.points.map((point) => (
              <li key={point} className="flex items-start gap-3 text-sm text-zinc-300 font-light">
                <Check className="w-4 h-4 mt-0.5 text-[#C5A059] shrink-0" />
                {point}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Reveal>
  );
}

function SectorMatrix() {
  return (
    <section className="border-y border-zinc-800/60 bg-void-2/30 blueprint-grid">
      <div className="max-w-7xl mx-auto px-6 py-24 sm:py-28">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-3">
            <span className="w-10 h-px bg-[#C5A059]" /> Sector capability
          </p>
          <h2 className="mt-6 font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-zinc-100 leading-tight">
            Where we deliver
          </h2>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-800/60 border border-zinc-800/60">
          {SECTORS.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.08}>
              <div
                data-testid={`sector-card-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="group h-full bg-void p-7 hover:bg-void-2 transition-colors duration-500"
              >
                <span className="block w-8 h-px bg-zinc-700 group-hover:bg-[#C5A059] group-hover:w-12 transition-all duration-500" />
                <h3 className="mt-5 font-display text-lg font-bold text-zinc-100">{s.name}</h3>
                <p className="mt-3 text-xs text-zinc-500 leading-relaxed font-light">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-24 sm:py-28">
      <Reveal>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-3">
          <span className="w-10 h-px bg-[#C5A059]" /> How we engage
        </p>
        <h2 className="mt-6 font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-zinc-100 leading-tight">
          From first conversation to handover
        </h2>
      </Reveal>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
        {PROCESS.map((step, i) => (
          <Reveal key={step.num} delay={i * 0.1}>
            <div data-testid={`process-step-${step.num}`} className="border-t-2 border-zinc-800 pt-6 relative">
              <span className="absolute -top-[2px] left-0 w-10 h-[2px] bg-[#C5A059]" />
              <span className="font-mono text-xs text-zinc-600 tracking-[0.2em]">{step.num}</span>
              <h3 className="mt-3 font-display text-lg font-bold text-zinc-100">{step.title}</h3>
              <p className="mt-3 text-xs text-zinc-500 leading-relaxed font-light">{step.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <>
      <PageHeader
        overline="Our services"
        lines={["Core capabilities,", "delivered hands-on"]}
        intro="Three disciplines that cover the full life of a construction project — leadership at the front, protection in the detail, and independent oversight throughout."
        testid="services-headline"
      />
      <section className="max-w-7xl mx-auto px-6 pb-8">
        {SERVICES.map((s, i) => (
          <ServiceBlock key={s.id} service={s} flip={i % 2 === 1} />
        ))}
      </section>
      <SectorMatrix />
      <Process />
      <CTA title="Not sure which service fits? Let's talk it through" text="A short conversation is usually enough to scope the right level of support for your project." />
    </>
  );
}
